import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import type { SkillContestEvent } from '@tg/shared';
import { paths } from '@/app/paths';
import { useLocale } from '@/i18n/useLocale';
import { useSocketEvent } from '@/socket/socket';
import { useSession } from '@/stores/session';
import { useToasts } from '@/stores/toasts';
import { importLocalSaves } from './localSave';

/**
 * Skill games runtime (App): after signing in, the guest's local training saves go to the server (localSave.ts);
 * `skill:contest` — a competition the player entered was settled / refunded — becomes a quiet toast with the place
 * and the prize (its own notification, never mixed with bet results: no sound, no coins, one tap closes it).
 */
export function SkillListener(): null {
  const status = useSession((s) => s.status);
  const intl = useIntl();
  const l = useLocale();
  useEffect(() => {
    if (status === 'user') void importLocalSaves();
  }, [status]);
  useSocketEvent<[SkillContestEvent]>('skill:contest', ({ contest: c }) => {
    const me = c.me;
    if (!me) return;
    const state = c.status === 'refunded' ? 'refunded' : me.rank === null ? 'unsolved' : (me.payout ?? 0) > 0 ? 'prize' : 'rank';
    useToasts.getState().push({
      kind: state === 'prize' ? 'success' : 'notice',
      // a prize not above the entry is quiet (fair play: no celebration of a loss)
      sound: (me.payout ?? 0) > c.entry ? undefined : false,
      titleId: 'skill.toast.contest',
      textId: 'skill.toast.contestText',
      values: {
        game: intl.formatMessage({ id: `common.game.${c.game}` }),
        state,
        rank: me.rank ?? 0,
        entries: c.entries,
        payout: intl.formatNumber(me.payout ?? 0),
      },
      link: { to: paths.game(l, c.game), labelId: 'skill.toast.open' },
    });
  });
  return null;
}
